import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Chip, Toolbar, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, TableSortLabel, Paper, Box, OutlinedInput, InputAdornment, FormControl, InputLabel, Select, MenuItem, Typography, AppBar, Tab, Tabs, IconButton, FormControlLabel } from '@mui/material';
import { Clear } from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import { format } from 'date-fns';
import buildUrl from '../buildUrl';
import DataColumn from '../dataTable/DataColumn';
import useSearch from '../hooks/useSearch';
import useSnackbar from '../hooks/useSnackbar';
import useApi from '../hooks/useApi';
import CenteredProgress from '../loadingOverlay/CenteredProgress';
import LoadingOverlay from '../loadingOverlay/LoadingOverlay';
import { useTheme } from '@mui/material/styles';
import RenderPeriodDateRange from '../account/reportingPeriod/RenderPeriodDateRange';
import Picker from '../picker/Picker';
import Android12Switch from '../editor/selects/ArchiveToggle';
import SurveysDashboard from './SurveysDashboard';
import useSuspenseFetch from '../hooks/useSuspenseFetch';

const rowsPerPageOptions = [10, 25, 50];

const columns = [
    { name: 'name', label: 'Name', orderDesc: false, dataType: 'text', width: '25%' },
    { name: 'status', label: 'Status', orderDesc: false, dataType: 'status', width: 120 },
    { name: 'created', label: 'Created', align: 'right', orderDesc: false, dataType: 'dateTime' },
    { name: 'modified', label: 'Modified', align: 'right', orderDesc: false, dataType: 'dateTime' },
    { name: 'resultsCount', label: 'Responses', align: 'right', orderDesc: true, dataType: 'number' }
];

const Channels = ({ survey }) => {
    return (
        <Box sx={{ gap: 1 }}>
            {survey.EmailsCount > 0 ? <Chip size="small" label={`Email${survey.EmailsCount > 1 ? 's' : ''}`} /> : null}
            {survey.qrCodesCount > 0 ? <Chip size="small" label={`QR Code${survey.EmailsCount > 1 ? 's' : ''}`} /> : null}
        </Box>
    );
};

const Surveys = ({ selectedPeriods, prevSelectedPeriods, compare, topOffset, filterTopics, onChangeTopicsFilter }) => {
    const { data: topics } = useSuspenseFetch('topics');
    const navigate = useNavigate();
    const theme = useTheme();
    const [surveyReports, setSurveyReports] = useState(null);
    const [surveyData, setSurveyData] = useState(null);
    const [count, setCount] = useState(0);
    const [searchValue, setSearchValue] = useState('');
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
    const [page, setPage] = useState(0);
    const [orderBy, setOrderBy] = useState('modified');
    const [orderDesc, setOrderDesc] = useState(true);
    const [isInitialising, setIsInitialising] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [archived, setArchived] = useState(false);
    const [tempSearchValue, setTempSearchValue] = useSearch(setSearchValue);
    const { handleGet, handlePut } = useApi();
    const [filterByPeriod, setFilterByPeriod] = useState('none');
    const [tab, setTab] = useState('navigation');
    const { showSnackbar } = useSnackbar();

    const handleFetchCount = async () => {
        const filterByPeriodDates = filterByPeriod !== 'none' ? filterByPeriod === 'a' ? selectedPeriods[0] : selectedPeriods[1] : null;

        const from = filterByPeriodDates !== null ? format(filterByPeriodDates[0], 'yyyy-MM-dd') : null;
        const to = filterByPeriodDates !== null ? format(filterByPeriodDates[1], 'yyyy-MM-dd') : null;

        const topicIds = filterTopics?.map(e => e.id);

        const params = {
            search: searchValue,
            archived,
            ...(from && { fromDate: from }),
            ...(to && { toDate: to }),
            ...(filterTopics && { topicIds })
        };

        const url = buildUrl('surveys/reports/count', params);
        const response = await handleGet(url);

        if (!response.ok) {
            return;
        }

        const data = await response.json();
        setCount(data);
    };

    const handleFetchSurveyReports = async () => {
        setIsLoading(true);

        const filterByPeriodDates = filterByPeriod !== 'none' ? filterByPeriod === 'a' ? selectedPeriods[0] : selectedPeriods[1] : null;

        const from = filterByPeriodDates !== null ? format(filterByPeriodDates[0], 'yyyy-MM-dd') : null;
        const to = filterByPeriodDates !== null ? format(filterByPeriodDates[1], 'yyyy-MM-dd') : null;

        const topicIds = filterTopics?.map(e => e.id);

        const params = {
            search: searchValue,
            archived,
            skip: page * rowsPerPage,
            take: rowsPerPage,
            orderBy,
            ...(from && { fromDate: from }),
            ...(to && { toDate: to }),
            isDescending: orderDesc,
            ...(filterTopics?.length > 0 && { topicIds })
        };

        const url = buildUrl('surveys/reports', params);
        const response = await handleGet(url);

        if (!response.ok) {
            return;
        }

        const data = await response.json();
        setSurveyReports(data);
        setIsLoading(false);
    };

    // Fetch JSONs separately from the main return due to potential embedded image size concerns
    const handleFetchSurveyData = async () => {
        const filterByPeriodDates = filterByPeriod !== 'none' ? filterByPeriod === 'a' ? selectedPeriods[0] : selectedPeriods[1] : null;

        const from = filterByPeriodDates !== null ? format(filterByPeriodDates[0], 'yyyy-MM-dd') : null;
        const to = filterByPeriodDates !== null ? format(filterByPeriodDates[1], 'yyyy-MM-dd') : null;

        const topicIds = filterTopics?.map(e => e.id);

        const params = {
            search: searchValue,
            archived,
            orderBy,
            ...(from && { fromDate: from }),
            ...(to && { toDate: to }),
            isDescending: orderDesc,
            ...(filterTopics?.length > 0 && { topicIds })
        };

        const url = buildUrl('surveys/reports/data', params);
        const response = await handleGet(url);

        if (!response.ok) {
            return;
        }

        const data = await response.json();

        setSurveyData(data);
    };

    const handleSort = (field, defaultOrder) => {
        if (field === orderBy) {
            setOrderDesc(!orderDesc);
        }
        else {
            setOrderBy(field);
            setOrderDesc(defaultOrder);
        }
    };

    const handleUpdateTopics = async ({ survey, topics = null }) => {
        const topicIds = topics?.map(e => e.id);

        try {
            const response = await handlePut(`surveys/${survey.id}`, { topicIds });

            if (!response.ok) {
                const errorMessage = response.status === 404
                    ? 'Survey not found'
                    : 'Failed to update Topics';

                showSnackbar(errorMessage, 'error');
            }
        }
        catch (error) {
            console.error('Error updating Topics:', error);
            showSnackbar('An error occurred while updating Topics.', 'error');
        }
    };

    useEffect(() => {
        surveyReports && setIsInitialising(false);
    }, [surveyReports]);

    useEffect(() => {
        tab === 'dashboard' && handleFetchSurveyData();
    }, [tab]);

    useEffect(() => {
        handleFetchCount();
    }, [searchValue, filterByPeriod, filterTopics, archived]);

    useEffect(() => {
        setPage(0);
    }, [searchValue, orderBy, orderDesc, rowsPerPage, filterByPeriod, filterTopics, archived]);

    useEffect(() => {
        handleFetchSurveyReports();
    }, [searchValue, orderBy, orderDesc, rowsPerPage, page, filterByPeriod, filterTopics, archived]);

    useEffect(() => {
        if (filterByPeriod !== 'none') {
            if (filterByPeriod === 'a' && (selectedPeriods[0] !== prevSelectedPeriods[0]) || filterByPeriod === 'b' && (selectedPeriods[1] !== prevSelectedPeriods[1])) {
                setIsLoading(true);
                setPage(0);
                handleFetchCount();
                handleFetchSurveyReports();
            }
        }
    }, [selectedPeriods]);

    if (isInitialising) {
        return <LoadingOverlay />;
    }

    return (
        <>
            <AppBar
                position="sticky"
                color="inherit"
                sx={{
                    top: topOffset,
                    flexShrink: 0, // fixes a bug causing height to shrink on scroll
                    zIndex: theme.zIndex.appBar - 1,
                    borderTop: 1,
                    borderColor: theme.palette.divider
                }}
            >
                <Tabs value={tab} onChange={(e, value) => setTab(value)}>
                    <Tab label="Find a Report" value="navigation" />
                    <Tab label="Insights" value="dashboard" />
                </Tabs>
            </AppBar>
            <Box sx={{ p: 2 }}>
                {tab === 'dashboard' ? (
                    <SurveysDashboard
                        data={surveyData}
                    />
                ) : (
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <Paper>
                                <Toolbar disableGutters sx={{ mx: 1 }}>
                                    <OutlinedInput
                                        value={tempSearchValue}
                                        onChange={e => setTempSearchValue(e.target.value)}
                                        placeholder="Search..."
                                        size="small"
                                        sx={{ m: 1 }}
                                        endAdornment={(
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setTempSearchValue('')}
                                                    edge="end"
                                                    disabled={!Boolean(tempSearchValue)}
                                                >
                                                    <Clear />
                                                </IconButton>
                                            </InputAdornment>
                                        )}
                                    />
                                    <FormControl margin="none">
                                        <InputLabel id="select-period-dates-label">Filter by Period</InputLabel>
                                        <Select
                                            labelId="select-period-dates-label"
                                            id="select-period-dates"
                                            value={filterByPeriod}
                                            label="Filter by Period"
                                            onChange={e => setFilterByPeriod(e.target.value)}
                                            size="small"
                                            sx={{ minWidth: 120 }}
                                        >
                                            <MenuItem value="none">None</MenuItem>
                                            {selectedPeriods.length > 0 && (
                                                <MenuItem value="a">
                                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mr: 1, display: 'inline' }}>
                                                        Period A:
                                                    </Typography>
                                                    <RenderPeriodDateRange
                                                        period={selectedPeriods[0]}
                                                    />
                                                </MenuItem>
                                            )}
                                            {(selectedPeriods.length > 1 && compare) && (
                                                <MenuItem value="b">
                                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mr: 1, display: 'inline' }}>
                                                        Period B:
                                                    </Typography>
                                                    <RenderPeriodDateRange
                                                        period={selectedPeriods[1]}
                                                    />
                                                </MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>
                                    <FormControlLabel
                                        sx={{ ml: 1 }}
                                        control={(
                                            <Android12Switch
                                                checked={archived}
                                                onChange={e => setArchived(e.target.checked)}
                                            />
                                        )}
                                        label="Archived"
                                    />
                                </Toolbar>
                                <Divider />
                                {isLoading ? (
                                    <Box sx={{ py: 4 }}>
                                        <CenteredProgress colour="primary" />
                                    </Box>
                                ) : (
                                    <>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        {columns.map(col => (
                                                            <TableCell
                                                                key={col.name}
                                                                component="th"
                                                                scope="row"
                                                                align={col.align}
                                                                width={col.width || 'auto'}
                                                            >
                                                                <TableSortLabel
                                                                    active={orderBy === col.name}
                                                                    direction={orderDesc ? 'desc' : 'asc'}
                                                                    onClick={() => handleSort(col.name, col.orderDesc)}
                                                                >
                                                                    {col.label}
                                                                </TableSortLabel>
                                                            </TableCell>
                                                        ))}
                                                        <TableCell scope="row" align="left">Channels</TableCell>
                                                        <TableCell scope="row" align="left" sx={{ width: '20%' }}>Topics</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {surveyReports?.length > 0 ? surveyReports.map(survey => (
                                                        <TableRow
                                                            key={survey.id}
                                                            hover={true}
                                                        >
                                                            {columns.map(col => (
                                                                <DataColumn
                                                                    key={col.name}
                                                                    align={col.align}
                                                                    dataType={col.dataType}
                                                                    value={survey[col.name]}
                                                                    cursor={col.name === 'name' ? 'pointer' : 'default'}
                                                                    onClick={() => col.name === 'name' && navigate(`/newreports/surveys/${survey.id}`)}
                                                                />
                                                            ))}
                                                            <TableCell align="left">
                                                                <Channels survey={survey} />
                                                            </TableCell>
                                                            <TableCell align="left" sx={{ p: 0 }}>
                                                                <Picker
                                                                    availableItems={topics}
                                                                    selectedItems={survey.topics}
                                                                    onChangeItems={topics => handleUpdateTopics({ survey, topics })}
                                                                    onClickItem={topic => onChangeTopicsFilter(topic)}
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    )) : (
                                                        <TableRow>
                                                            <TableCell colSpan={columns.length + 2} align="center" sx={{ p: 5 }}>
                                                                No data
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        <TablePagination
                                            rowsPerPageOptions={rowsPerPageOptions}
                                            component="div"
                                            count={count}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            onPageChange={(e, newPage) => setPage(newPage)}
                                            onRowsPerPageChange={e => setRowsPerPage(e.target.value)}
                                        />
                                    </>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                )}
            </Box>
        </>
    );
};

export default Surveys;